import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './ContactRequests.css';

interface ContactRequest {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  submittedAt: Date;
  status?: 'new' | 'read' | 'responded';
}

const ContactRequests: React.FC = () => {
  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'new' | 'read' | 'responded'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);

  useEffect(() => {
    fetchContactRequests();
  }, []);

  const fetchContactRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const q = query(collection(db, 'contactRequests'), orderBy('submittedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const requestsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate() : new Date(data.submittedAt),
          status: data.status || 'new'
        };
      }) as ContactRequest[];
      
      setRequests(requestsData);
    } catch (err: any) {
      console.error('Error fetching contact requests:', err);
      setError(`Failed to load contact requests: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, newStatus: 'new' | 'read' | 'responded') => {
    try {
      await updateDoc(doc(db, 'contactRequests', requestId), {
        status: newStatus
      });

      setRequests(requests.map(req =>
        req.id === requestId ? { ...req, status: newStatus } : req
      ));

      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (err: any) {
      console.error('Error updating request status:', err);
      setError(`Failed to update status: ${err.message || 'Unknown error'}`);
    }
  };

  const handleViewRequest = (request: ContactRequest) => {
    setSelectedRequest(request);
    if (request.status === 'new') {
      updateRequestStatus(request.id, 'read');
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = 
      req.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.message?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterStatus === 'all' || req.status === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const getStatusCounts = () => {
    return {
      all: requests.length,
      new: requests.filter(r => r.status === 'new').length,
      read: requests.filter(r => r.status === 'read').length,
      responded: requests.filter(r => r.status === 'responded').length
    };
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="contact-requests">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading contact requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-requests">
      <div className="page-header">
        <div>
          <h1>Contact Requests</h1>
          <p className="page-subtitle">
            Manage and respond to user inquiries. Total requests: <strong>{requests.length}</strong>
          </p>
        </div>
        <button onClick={fetchContactRequests} className="refresh-btn" disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, subject, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-buttons">
          <button
            className={filterStatus === 'all' ? 'active' : ''}
            onClick={() => setFilterStatus('all')}
          >
            All ({statusCounts.all})
          </button>
          <button
            className={filterStatus === 'new' ? 'active' : ''}
            onClick={() => setFilterStatus('new')}
          >
            🔴 New ({statusCounts.new})
          </button>
          <button
            className={filterStatus === 'read' ? 'active' : ''}
            onClick={() => setFilterStatus('read')}
          >
            👁️ Read ({statusCounts.read})
          </button>
          <button
            className={filterStatus === 'responded' ? 'active' : ''}
            onClick={() => setFilterStatus('responded')}
          >
            ✅ Responded ({statusCounts.responded})
          </button>
        </div>
      </div>

      {/* Requests Grid */}
      <div className="requests-layout">
        {/* Requests List */}
        <div className="requests-list">
          {filteredRequests.length === 0 ? (
            <div className="no-results">
              <p>No contact requests found matching your criteria</p>
            </div>
          ) : (
            filteredRequests.map(request => (
              <div
                key={request.id}
                className={`request-card ${selectedRequest?.id === request.id ? 'selected' : ''} ${request.status === 'new' ? 'unread' : ''}`}
                onClick={() => handleViewRequest(request)}
              >
                <div className="request-header">
                  <div className="request-sender">
                    <div className="sender-avatar">
                      {request.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="sender-info">
                      <h4>{request.name}</h4>
                      <p className="sender-email">{request.email}</p>
                    </div>
                  </div>
                  <span className={`status-badge status-${request.status}`}>
                    {request.status === 'new' && '🔴'}
                    {request.status === 'read' && '👁️'}
                    {request.status === 'responded' && '✅'}
                  </span>
                </div>
                <div className="request-subject">
                  <strong>{request.subject}</strong>
                </div>
                <div className="request-preview">
                  {request.message.substring(0, 100)}
                  {request.message.length > 100 && '...'}
                </div>
                <div className="request-time">
                  {new Date(request.submittedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Request Detail Panel */}
        <div className="request-detail">
          {selectedRequest ? (
            <>
              <div className="detail-header">
                <div className="detail-sender">
                  <div className="sender-avatar large">
                    {selectedRequest.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <h3>{selectedRequest.name}</h3>
                    <a href={`mailto:${selectedRequest.email}`} className="sender-email">
                      {selectedRequest.email}
                    </a>
                  </div>
                </div>
                <div className="detail-actions">
                  <select
                    value={selectedRequest.status}
                    onChange={(e) => updateRequestStatus(selectedRequest.id, e.target.value as any)}
                    className="status-select"
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="responded">Responded</option>
                  </select>
                </div>
              </div>

              <div className="detail-meta">
                <div className="meta-item">
                  <span className="meta-label">Subject:</span>
                  <span className="meta-value">{selectedRequest.subject}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Received:</span>
                  <span className="meta-value">
                    {new Date(selectedRequest.submittedAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>

              <div className="detail-message">
                <h4>Message:</h4>
                <p>{selectedRequest.message}</p>
              </div>

              <div className="detail-actions-footer">
                <a 
                  href={`mailto:${selectedRequest.email}?subject=Re: ${selectedRequest.subject}`}
                  className="reply-btn"
                  onClick={() => updateRequestStatus(selectedRequest.id, 'responded')}
                >
                  📧 Reply via Email
                </a>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <p>Select a contact request to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactRequests;
